import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const THEME_KEY = "hookora_theme";

const emptyForm = {
  audience: "",
  goal: "",
  offer: "",
  painPoint: "",
  desiredOutcome: "",
  angle: "",
  callToAction: "",
  tone: "Confident",
  brandVoice: "",
  keywords: "",
  avoidPhrases: "",
  extraContext: "",
  language: "en",
};

const toneOptions = [
  { value: "Confident", key: "confident", fallback: "Confident" },
  { value: "Professional", key: "professional", fallback: "Professional" },
  { value: "Bold", key: "bold", fallback: "Bold" },
  { value: "Conversational", key: "conversational", fallback: "Conversational" },
  { value: "Warm", key: "warm", fallback: "Warm" },
  { value: "Persuasive", key: "persuasive", fallback: "Persuasive" },
  { value: "Direct", key: "direct", fallback: "Direct" },
];

const languageOptions = [
  { value: "en", key: "en", fallback: "English" },
  { value: "es", key: "es", fallback: "Spanish" },
  { value: "fr", key: "fr", fallback: "French" },
  { value: "pt", key: "pt", fallback: "Portuguese" },
];

const OfferPositioningTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());
  const [form, setForm] = useState({
    ...emptyForm,
    platform: sharedPlatform || "",
  });
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      platform: sharedPlatform || prev.platform,
    }));
  }, [sharedPlatform]);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      panelBg: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
      panelBorder: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      panelShadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.30)"
        : "0 10px 30px rgba(15, 23, 42, 0.06)",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputBorder: isDark ? "rgba(148,163,184,0.24)" : "#d1d5db",
      inputText: isDark ? "#f8fafc" : "#111827",
      title: isDark ? "#f8fafc" : "#0f172a",
      text: isDark ? "#e2e8f0" : "#111827",
      muted: isDark ? "#94a3b8" : "#64748b",
      subCardBg: isDark ? "rgba(15,23,42,0.88)" : "#f8fafc",
      subCardBorder: isDark ? "rgba(148,163,184,0.14)" : "#e5e7eb",
      chipBg: isDark ? "rgba(30,41,59,0.95)" : "#eef2ff",
      chipText: isDark ? "#cbd5e1" : "#4338ca",
      buttonBg: "#2563eb",
      buttonText: "#ffffff",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      errorBg: isDark ? "rgba(127,29,29,0.22)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248,113,113,0.32)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c",
      successBg: "#16a34a",
      sectionBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      sectionBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      strongBg: isDark ? "rgba(16,185,129,0.14)" : "#ecfdf5",
      strongBorder: isDark ? "rgba(52,211,153,0.24)" : "#a7f3d0",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "11px 12px",
    borderRadius: "12px",
    border: `1px solid ${colors.inputBorder}`,
    background: colors.inputBg,
    color: colors.inputText,
    boxSizing: "border-box",
    outline: "none",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    color: colors.text,
    fontWeight: "700",
    fontSize: "14px",
  };

  const helperStyle = {
    color: colors.muted,
    fontSize: "12px",
    lineHeight: "1.5",
    marginTop: "6px",
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "platform") {
      setSharedPlatform(value);
    }
  };

  const buildPositioningText = (item) => {
    return [
      item.positioning_angle
        ? `${t("studioTools.offerPositioningTool.results.positioningAngle")}: ${item.positioning_angle}`
        : "",
      item.value_proposition
        ? `${t("studioTools.offerPositioningTool.results.valueProposition")}: ${item.value_proposition}`
        : "",
      item.who_its_for
        ? `${t("studioTools.offerPositioningTool.results.whoItsFor")}: ${item.who_its_for}`
        : "",
      item.problem_frame
        ? `${t("studioTools.offerPositioningTool.results.problemFrame")}: ${item.problem_frame}`
        : "",
      item.differentiator
        ? `${t("studioTools.offerPositioningTool.results.differentiator")}: ${item.differentiator}`
        : "",
      item.message_example
        ? `${t("studioTools.offerPositioningTool.results.messageExample")}: ${item.message_example}`
        : "",
    ]
      .filter(Boolean)
      .join("\n\n");
  };

  const handleCopy = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1800);
    } catch (err) {
      console.error("Failed to copy positioning output:", err);
      setError(t("studioTools.offerPositioningTool.messages.copyFailed"));
    }
  };

  const handleReset = () => {
    setForm({
      ...emptyForm,
      platform: sharedPlatform || "",
    });
    setPositions([]);
    setError("");
    setCopiedIndex(null);
  };

  const handleGenerate = async () => {
    const topic = sharedTopic.trim();
    const offer = form.offer.trim();

    if (!topic && !offer) {
      setError(t("studioTools.offerPositioningTool.messages.topicOrOfferRequired"));
      return;
    }

    try {
      setLoading(true);
      setError("");
      setPositions([]);

      const response = await API.post("/studio/offer-positioning", {
        topic: sharedTopic || form.offer,
        platform: form.platform || sharedPlatform || "Website",
        audience: form.audience,
        goal: form.goal,
        offer: form.offer,
        pain_point: form.painPoint,
        desired_outcome: form.desiredOutcome,
        angle: form.angle,
        call_to_action: form.callToAction,
        tone: form.tone,
        keywords: form.keywords,
        brand_voice: form.brandVoice,
        avoid_phrases: form.avoidPhrases,
        extra_context: form.extraContext,
        language: form.language,
      });

      setPositions(response?.data?.positions || []);
    } catch (err) {
      console.error("Failed to generate offer positioning:", err);
      setError(
        err?.response?.data?.detail ||
          t("studioTools.offerPositioningTool.messages.generateFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <MotionSurface>
    <div style={{ display: "grid", gap: "18px" }}>
      <div
        style={{
          padding: "18px",
          borderRadius: "18px",
          border: `1px solid ${colors.panelBorder}`,
          background: colors.panelBg,
          boxShadow: colors.panelShadow,
        }}
      >
        <div style={{ marginBottom: "18px" }}>
          <h2
            style={{
              margin: 0,
              color: colors.title,
              fontSize: "22px",
              lineHeight: "1.15",
            }}
          >
            {t("studioTools.offerPositioningTool.title")}
          </h2>
          <p
            style={{
              margin: "8px 0 0",
              color: colors.muted,
              lineHeight: "1.6",
              fontSize: "14px",
            }}
          >
            {t("studioTools.offerPositioningTool.description")}
          </p>
        </div>

        {error ? (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px 14px",
              borderRadius: "12px",
              border: `1px solid ${colors.errorBorder}`,
              background: colors.errorBg,
              color: colors.errorText,
              lineHeight: "1.55",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
            marginBottom: "14px",
          }}
        >
          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.topic.label")}</label>
            <input
              type="text"
              value={sharedTopic}
              onChange={(e) => setSharedTopic(e.target.value)}
              placeholder={t("studioTools.offerPositioningTool.fields.topic.placeholder")}
              style={inputStyle}
            />
            <div style={helperStyle}>
              {t("studioTools.offerPositioningTool.fields.topic.hint")}
            </div>
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.platform.label")}</label>
            <input
              type="text"
              value={form.platform}
              onChange={(e) => handleChange("platform", e.target.value)}
              placeholder={t("studioTools.offerPositioningTool.fields.platform.placeholder")}
              style={inputStyle}
            />
            <div style={helperStyle}>
              {t("studioTools.offerPositioningTool.fields.platform.hint")}
            </div>
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.tone.label")}</label>
            <select
              value={form.tone}
              onChange={(e) => handleChange("tone", e.target.value)}
              style={inputStyle}
            >
              {toneOptions.map((tone) => (
                <option key={tone.value} value={tone.value}>
                  {t(`studioTools.offerPositioningTool.toneOptions.${tone.key}`, tone.fallback)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.language.label")}</label>
            <select
              value={form.language}
              onChange={(e) => handleChange("language", e.target.value)}
              style={inputStyle}
            >
              {languageOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {t(`studioTools.offerPositioningTool.languageOptions.${item.key}`, item.fallback)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: "14px" }}>
          <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.offer.label")}</label>
          <textarea
            value={form.offer}
            onChange={(e) => handleChange("offer", e.target.value)}
            placeholder={t("studioTools.offerPositioningTool.fields.offer.placeholder")}
            rows={4}
            style={{
              ...inputStyle,
              resize: "vertical",
            }}
          />
          <div style={helperStyle}>
            {t("studioTools.offerPositioningTool.fields.offer.hint")}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
            marginBottom: "14px",
          }}
        >
          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.audience.label")}</label>
            <input
              type="text"
              value={form.audience}
              onChange={(e) => handleChange("audience", e.target.value)}
              placeholder={t("studioTools.offerPositioningTool.fields.audience.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.goal.label")}</label>
            <input
              type="text"
              value={form.goal}
              onChange={(e) => handleChange("goal", e.target.value)}
              placeholder={t("studioTools.offerPositioningTool.fields.goal.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.painPoint.label")}</label>
            <input
              type="text"
              value={form.painPoint}
              onChange={(e) => handleChange("painPoint", e.target.value)}
              placeholder={t("studioTools.offerPositioningTool.fields.painPoint.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.desiredOutcome.label")}</label>
            <input
              type="text"
              value={form.desiredOutcome}
              onChange={(e) => handleChange("desiredOutcome", e.target.value)}
              placeholder={t("studioTools.offerPositioningTool.fields.desiredOutcome.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.angle.label")}</label>
            <input
              type="text"
              value={form.angle}
              onChange={(e) => handleChange("angle", e.target.value)}
              placeholder={t("studioTools.offerPositioningTool.fields.angle.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.callToAction.label")}</label>
            <input
              type="text"
              value={form.callToAction}
              onChange={(e) => handleChange("callToAction", e.target.value)}
              placeholder={t("studioTools.offerPositioningTool.fields.callToAction.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.brandVoice.label")}</label>
            <input
              type="text"
              value={form.brandVoice}
              onChange={(e) => handleChange("brandVoice", e.target.value)}
              placeholder={t("studioTools.offerPositioningTool.fields.brandVoice.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.keywords.label")}</label>
            <input
              type="text"
              value={form.keywords}
              onChange={(e) => handleChange("keywords", e.target.value)}
              placeholder={t("studioTools.offerPositioningTool.fields.keywords.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.avoidPhrases.label")}</label>
            <input
              type="text"
              value={form.avoidPhrases}
              onChange={(e) => handleChange("avoidPhrases", e.target.value)}
              placeholder={t("studioTools.offerPositioningTool.fields.avoidPhrases.placeholder")}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label style={labelStyle}>{t("studioTools.offerPositioningTool.fields.extraContext.label")}</label>
          <textarea
            value={form.extraContext}
            onChange={(e) => handleChange("extraContext", e.target.value)}
            placeholder={t("studioTools.offerPositioningTool.fields.extraContext.placeholder")}
            rows={4}
            style={{
              ...inputStyle,
              resize: "vertical",
            }}
          />
        </div>

        <ActionRow>
          <AIActionButton isDark={isDark}
            type="button"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading
              ? t("studioTools.offerPositioningTool.actions.generating")
              : t("studioTools.offerPositioningTool.actions.generate")}
          </AIActionButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={handleReset}
            disabled={loading}
          >
            {t("studioTools.offerPositioningTool.actions.reset")}
          </UtilityButton>
        </ActionRow>
      </div>

      <div
        style={{
          padding: "18px",
          borderRadius: "18px",
          border: `1px solid ${colors.panelBorder}`,
          background: colors.panelBg,
          boxShadow: colors.panelShadow,
        }}
      >
        <div style={{ marginBottom: "14px" }}>
          <h3
            style={{
              margin: 0,
              color: colors.title,
              fontSize: "18px",
            }}
          >
            {t("studioTools.offerPositioningTool.results.title")}
          </h3>
          <p
            style={{
              margin: "8px 0 0",
              color: colors.muted,
              lineHeight: "1.6",
              fontSize: "14px",
            }}
          >
            {t("studioTools.offerPositioningTool.results.description")}
          </p>
        </div>

        {loading ? (
          <ToolResultSkeleton isDark={isDark} count={3} />
        ) : positions.length === 0 ? (
          <div
            style={{
              padding: "16px",
              borderRadius: "14px",
              border: `1px solid ${colors.subCardBorder}`,
              background: colors.subCardBg,
              color: colors.muted,
              lineHeight: "1.6",
              fontSize: "14px",
            }}
          >
            {t("studioTools.offerPositioningTool.results.empty")}
          </div>
        ) : (
          <div style={{ display: "grid", gap: "14px" }}>
            {positions.map((item, index) => {
              const fullText = buildPositioningText(item);
              const title =
                item.variant_name ||
                item.positioning_angle ||
                t("studioTools.offerPositioningTool.results.fallbackTitle", {
                  number: index + 1,
                });

              return (
                <ResultCard
                  key={`${title}-${index}`}
                  colors={{
                    border: colors.subCardBorder,
                    cardBgStrong: colors.subCardBg,
                    shadowSoft: colors.panelShadow,
                  }}
                  index={index}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "12px",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      marginBottom: "12px",
                    }}
                  >
                    <div>
                      <h4
                        style={{
                          margin: 0,
                          color: colors.title,
                          fontSize: "16px",
                          lineHeight: "1.3",
                        }}
                      >
                        {title}
                      </h4>

                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                          marginTop: "8px",
                        }}
                      >
                        {item.label ? (
                          <span
                            style={{
                              padding: "4px 9px",
                              borderRadius: "999px",
                              background: colors.chipBg,
                              color: colors.chipText,
                              fontSize: "12px",
                              fontWeight: "700",
                            }}
                          >
                            {item.label}
                          </span>
                        ) : null}

                        {typeof item.score === "number" ? (
                          <span
                            style={{
                              padding: "4px 9px",
                              borderRadius: "999px",
                              background: colors.chipBg,
                              color: colors.chipText,
                              fontSize: "12px",
                              fontWeight: "700",
                            }}
                          >
                            {t("studioTools.offerPositioningTool.results.score", { score: item.score })}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {item.positioning_angle ? (
                    <div
                      style={{
                        padding: "12px",
                        borderRadius: "12px",
                        background: colors.strongBg,
                        border: `1px solid ${colors.strongBorder}`,
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ ...labelStyle, marginBottom: "4px" }}>
                        {t("studioTools.offerPositioningTool.results.positioningAngle")}
                      </div>
                      <div
                        style={{
                          color: colors.text,
                          fontWeight: "700",
                          lineHeight: "1.6",
                          fontSize: "15px",
                        }}
                      >
                        {item.positioning_angle}
                      </div>
                    </div>
                  ) : null}

                  <div style={{ display: "grid", gap: "10px" }}>
                    {item.value_proposition ? (
                      <div
                        style={{
                          padding: "12px",
                          borderRadius: "12px",
                          background: colors.sectionBg,
                          border: `1px solid ${colors.sectionBorder}`,
                        }}
                      >
                        <div style={{ ...labelStyle, marginBottom: "4px" }}>
                          {t("studioTools.offerPositioningTool.results.valueProposition")}
                        </div>
                        <div
                          style={{
                            color: colors.text,
                            lineHeight: "1.7",
                            fontSize: "14px",
                          }}
                        >
                          {item.value_proposition}
                        </div>
                      </div>
                    ) : null}

                    {item.who_its_for ? (
                      <div>
                        <div style={{ ...labelStyle, marginBottom: "4px" }}>
                          {t("studioTools.offerPositioningTool.results.whoItsFor")}
                        </div>
                        <div
                          style={{
                            color: colors.text,
                            lineHeight: "1.7",
                            fontSize: "14px",
                          }}
                        >
                          {item.who_its_for}
                        </div>
                      </div>
                    ) : null}

                    {item.problem_frame ? (
                      <div>
                        <div style={{ ...labelStyle, marginBottom: "4px" }}>
                          {t("studioTools.offerPositioningTool.results.problemFrame")}
                        </div>
                        <div
                          style={{
                            color: colors.text,
                            lineHeight: "1.7",
                            fontSize: "14px",
                          }}
                        >
                          {item.problem_frame}
                        </div>
                      </div>
                    ) : null}

                    {item.differentiator ? (
                      <div>
                        <div style={{ ...labelStyle, marginBottom: "4px" }}>
                          {t("studioTools.offerPositioningTool.results.differentiator")}
                        </div>
                        <div
                          style={{
                            color: colors.text,
                            lineHeight: "1.7",
                            fontSize: "14px",
                          }}
                        >
                          {item.differentiator}
                        </div>
                      </div>
                    ) : null}

                    {item.message_example ? (
                      <div>
                        <div style={{ ...labelStyle, marginBottom: "4px" }}>
                          {t("studioTools.offerPositioningTool.results.messageExample")}
                        </div>
                        <div
                          style={{
                            color: colors.text,
                            whiteSpace: "pre-wrap",
                            lineHeight: "1.7",
                            fontSize: "14px",
                          }}
                        >
                          {item.message_example}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <ActionRow style={{ marginTop: "14px" }}>
                    <CopyButton
                      copied={copiedIndex === index}
                      type="button"
                      onClick={() => handleCopy(fullText, index)}
                    >
                      {copiedIndex === index
                        ? t("studioTools.offerPositioningTool.actions.copied")
                        : t("studioTools.offerPositioningTool.actions.copy")}
                    </CopyButton>

                    <SaveStudioAssetButton
                      toolType="offer_positioning"
                      title={title}
                      topic={sharedTopic || form.offer || "Offer Positioning"}
                      platform={form.platform || sharedPlatform || "Website"}
                      content={fullText}
                      meta={{
                        source_tool: "offer_positioning",
                        variant_name: item.variant_name || "",
                        positioning_angle: item.positioning_angle || "",
                        value_proposition: item.value_proposition || "",
                        who_its_for: item.who_its_for || "",
                        problem_frame: item.problem_frame || "",
                        differentiator: item.differentiator || "",
                        message_example: item.message_example || "",
                        score: item.score,
                        label: item.label,
                      }}
                    />
                  </ActionRow>
                </ResultCard>
              );
            })}
          </div>
        )}
      </div>
    </div>
    </MotionSurface>
  );
};

export default OfferPositioningTool;